import { useState } from "react";
import api, { invalidateCsrfToken } from "@/lib/api";
import { useI18n } from "@/hooks/useI18n";
import { dictionary } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";

export const RequestForm = () => {
  const { lang } = useI18n();
  const data = dictionary.form;

  const [form, setForm] = useState({
    email: "",
    name: "",
    message: "",
    company: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim() || !form.message.trim()) {
      toast.error(data.required[lang]);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
        lang,
      });
      toast.success(data.success[lang]);
      setForm({ email: "", name: "", message: "", company: "" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      console.error("form submit failed", status, detail);
      if (status === 429) {
        const msg =
          lang === "ru"
            ? "Слишком много запросов. Попробуйте через минуту."
            : lang === "kz"
              ? "Сұраныс тым көп. Бір минуттан кейін қайталап көріңіз."
              : "Too many requests. Please try again in a minute.";
        toast.error(msg);
      } else if (status === 403 && detail === "csrf_failed") {
        invalidateCsrfToken();
        const msg =
          lang === "ru"
            ? "Сессия истекла. Обновите страницу и попробуйте снова."
            : lang === "kz"
              ? "Сессия аяқталды. Бетті жаңартып, қайталап көріңіз."
              : "Session expired. Please refresh the page and try again.";
        toast.error(msg);
      } else {
        toast.error(data.error[lang]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="form"
      data-testid="form-section"
      className="relative py-24 md:py-28 bg-[#F5F8FC] border-b border-[#E2E8F0]"
    >
      <div className="absolute top-0 right-0 w-[480px] h-[400px] bg-[#1857D6]/10 blur-[180px] pointer-events-none" />
      <div className="relative container mx-auto px-6 md:px-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span
              data-testid="form-label"
              className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#1857D6]"
            >
              ▍ {data.label[lang]}
            </span>
            <h2
              data-testid="form-title"
              className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0A1933] leading-[1.1]"
              style={{ fontFamily: "Manrope, IBM Plex Sans, sans-serif" }}
            >
              {data.title[lang]}
            </h2>
            <p className="mt-6 text-base lg:text-lg text-[#475569] leading-relaxed">
              {data.subtitle[lang]}
            </p>

            <div className="mt-10 flex items-start gap-4 p-5 bg-white border border-[#E2E8F0]">
              <div className="h-10 w-10 flex items-center justify-center bg-gradient-to-br from-[#0F2756] to-[#1857D6] text-white shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <p className="text-[13px] text-[#475569] leading-relaxed">
                {data.perk[lang]}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="request-form"
              onSubmit={onSubmit}
              noValidate
              className="bg-white border border-[#E2E8F0] p-6 sm:p-10 shadow-[0_20px_60px_-20px_rgba(15,39,86,0.18)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="text-[11px] tracking-[0.2em] uppercase text-[#0F2756] font-bold">
                    {data.fields.email[lang]} *
                  </label>
                  <Input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.kz"
                    className="mt-2 bg-white border-[#E2E8F0] focus-visible:border-[#1857D6] focus-visible:ring-0 text-[#0A1933] rounded-none h-12"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] tracking-[0.2em] uppercase text-[#0F2756] font-bold">
                    {data.fields.name[lang]} *
                  </label>
                  <Input
                    data-testid="form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder=""
                    className="mt-2 bg-white border-[#E2E8F0] focus-visible:border-[#1857D6] focus-visible:ring-0 text-[#0A1933] rounded-none h-12"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] tracking-[0.2em] uppercase text-[#0F2756] font-bold">
                    {data.fields.message[lang]} *
                  </label>
                  <Textarea
                    data-testid="form-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder=""
                    className="mt-2 bg-white border-[#E2E8F0] focus-visible:border-[#1857D6] focus-visible:ring-0 text-[#0A1933] rounded-none min-h-[140px]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] tracking-[0.2em] uppercase text-[#0F2756] font-bold">
                    {data.fields.company[lang]}
                  </label>
                  <Input
                    data-testid="form-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder=""
                    className="mt-2 bg-white border-[#E2E8F0] focus-visible:border-[#1857D6] focus-visible:ring-0 text-[#0A1933] rounded-none h-12"
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-[11px] text-[#475569] leading-relaxed max-w-sm">
                  {data.privacy[lang]}
                </p>
                <Button
                  data-testid="form-submit"
                  type="submit"
                  disabled={submitting}
                  className="group bg-[#0F2756] hover:bg-[#1857D6] text-white rounded-none h-14 px-8 font-bold tracking-wider uppercase text-sm disabled:opacity-60"
                >
                  {submitting ? data.sending[lang] : data.submit[lang]}
                  {!submitting && (
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestForm;
